import { useState, useCallback, useEffect } from 'react';
import { analyzeMultiplePdfs, fetchProperties } from '../utils/api';
import PdfAnalysisModal from './pdf_analysis_modal';
import PdfMigrationModal from './pdf_migration_modal';

/**
 * PDF 업로드 및 파싱 테스트 컴포넌트
 * PDF를 업로드하면 파싱된 내용을 확인할 수 있습니다.
 */
function PdfUploadTest({ onClose, onSuccess }) {
  const [sections, setSections] = useState([
    { id: 1, 상권: '', files: [] }
  ]);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingSectionId, setDraggingSectionId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null); // 분석 결과
  const [isAnalyzing, setIsAnalyzing] = useState(false); // 분석 중 상태
  const [상권목록, set상권목록] = useState([]); // 상권 목록
  const [isLoading상권목록, setIsLoading상권목록] = useState(false); // 상권 목록 로딩 중
  const [expandedSectionId, setExpandedSectionId] = useState(null); // 파일명 목록이 펼쳐진 구역 ID
  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false); // 마이그레이션 모달 열림 상태
  const [isMinimized, setIsMinimized] = useState(false); // 모달 최소화 상태
  const [progressInfo, setProgressInfo] = useState({ current: 0, total: 0, stage: '' }); // 진행 상황 정보
  
  // 진행 상황 추적
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    currentFile: '',
    stage: 'idle', // 'idle', 'parsing', 'formalizing', 'saving', 'completed'
    results: [] // { fileName, status, message }
  });

  // 상권 목록 로드
  useEffect(() => {
    async function load상권목록() {
      setIsLoading상권목록(true);
      try {
        const data = await fetchProperties();
        const unique상권 = Array.from(new Set(data.map((item) => item?.상권).filter(Boolean))).sort();
        set상권목록(unique상권);
      } catch (error) {
        console.error('상권 목록 로드 실패:', error);
        set상권목록([]);
      } finally {
        setIsLoading상권목록(false);
      }
    }
    load상권목록();
  }, []);

  // 상권 구역 추가
  const addSection = useCallback(() => {
    const newId = Math.max(...sections.map(s => s.id), 0) + 1;
    setSections(prev => [...prev, { id: newId, 상권: '', files: [] }]);
    setError(null);
  }, [sections]);

  // 상권 구역 삭제
  const removeSection = useCallback((sectionId) => {
    if (sections.length === 1) {
      setError('최소 1개의 구역이 필요합니다.');
      return;
    }
    setSections(prev => prev.filter(s => s.id !== sectionId));
    setError(null);
  }, [sections]);

  // 구역의 상권 변경
  const updateSection상권 = useCallback((sectionId, value) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, 상권: value } : s
    ));
    setError(null);
  }, []);

  // 파일 처리 공통 함수 (특정 구역에 파일 추가)
  const processFiles = useCallback((selectedFiles, sectionId) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const pdfFiles = Array.from(selectedFiles).filter(file => file.name.match(/\.(pdf)$/i));
    if (pdfFiles.length === 0) {
      setError('PDF 파일만 업로드 가능합니다.');
      return;
    }

    if (pdfFiles.length !== selectedFiles.length) {
      setError(`${selectedFiles.length - pdfFiles.length}개의 파일이 PDF가 아닙니다.`);
    }

    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, files: [...s.files, ...pdfFiles] }
        : s
    ));
    setError(null);
    setParsedData(null);
  }, []);

  // 구역에서 파일 제거
  const removeFileFromSection = useCallback((sectionId, fileIndex) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, files: s.files.filter((_, idx) => idx !== fileIndex) }
        : s
    ));
  }, []);

  const handleFileSelect = useCallback((e, sectionId) => {
    processFiles(e.target.files, sectionId);
    // input 초기화 (같은 파일 다시 선택 가능하도록)
    e.target.value = '';
  }, [processFiles]);

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = useCallback((e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDraggingSectionId(sectionId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // 드래그가 영역을 완전히 벗어났을 때만 false로 설정
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
      setDraggingSectionId(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDraggingSectionId(null);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      processFiles(droppedFiles, sectionId);
    }
  }, [processFiles]);

  // 분석 단계 (2단계 프로세스의 1단계)
  const handleAnalyze = useCallback(async () => {
    // 유효성 검사
    const validSections = sections.filter(s => s.상권.trim() && s.files.length > 0);
    
    if (validSections.length === 0) {
      setError('최소 1개의 구역에 상권과 파일을 입력해주세요.');
      return;
    }

    // 모든 구역의 상권이 입력되었는지 확인
    const empty상권Sections = sections.filter(s => s.files.length > 0 && !s.상권.trim());
    if (empty상권Sections.length > 0) {
      setError('파일이 있는 구역의 상권을 모두 입력해주세요.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);

    try {
      // 모든 파일 수집 (구역별 상권 정보 포함)
      const allFiles = [];
      const fileTo상권Map = {}; // 파일명 -> 상권 매핑
      
      validSections.forEach(section => {
        section.files.forEach(file => {
          allFiles.push(file);
          fileTo상권Map[file.name] = section.상권.trim();
        });
      });
      
      // 파일 개수 체크 (용량 제한 없음 - 내부에서 순차 처리)
      const totalFiles = allFiles.length;
      const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);
      const maxFiles = 2000;
      
      console.log(`업로드할 파일: ${totalFiles}개, 총 크기: ${(totalSize / 1024 / 1024).toFixed(2)}MB (용량 제한 없음, 순차 처리)`);
      
      if (totalFiles > maxFiles) {
        setError(`파일 개수가 너무 많습니다. 최대 ${maxFiles}개까지 업로드 가능합니다. (현재: ${totalFiles}개)`);
        setIsAnalyzing(false);
        return;
      }
      
      // 용량 제한 제거 - 프론트엔드에서 배치로 나눠서 순차 처리
      
      // 파일이 많을 경우 경고
      if (totalFiles > 500) {
        console.warn(`대량 파일 업로드: ${totalFiles}개 파일. 처리에 시간이 걸릴 수 있습니다.`);
      }

      // 주의: 현재 analyze-multiple API는 하나의 상권만 받음
      // 여러 구역이 있으면 첫 번째 구역의 상권을 사용 (임시)
      // TODO: 백엔드 API를 수정하여 파일별 상권을 받을 수 있도록 개선 필요
      const primary상권 = validSections[0]?.상권?.trim() || '';
      
      if (validSections.length > 1) {
        console.warn('여러 구역이 있습니다. 첫 번째 구역의 상권을 사용합니다:', primary상권);
      }

      // 진행 상황 추적을 위한 콜백 설정
      setProgressInfo({ current: 0, total: totalFiles, stage: '분석 시작' });
      
      // 진행 상황 업데이트 콜백
      const progressCallback = (current, total, stage) => {
        setProgressInfo({ current, total, stage: stage || '분석 중' });
      };
      
      // 모든 파일을 한 번에 분석
      const result = await analyzeMultiplePdfs(allFiles, primary상권, null, progressCallback);
      
      // 파일별 상권 정보를 분석 데이터에 추가
      if (result._analysisData && result._analysisData.pdfDataList) {
        result._analysisData.pdfDataList = result._analysisData.pdfDataList.map(pdf => ({
          ...pdf,
          상권: fileTo상권Map[pdf.fileName] || primary상권
        }));
        // 구역별 상권 정보도 저장
        result._analysisData.fileTo상권Map = fileTo상권Map;
      }

      setAnalysisResult(result);
      setProgressInfo({ current: totalFiles, total: totalFiles, stage: '완료' });
      // 작업 완료 시 자동으로 최대화
      setIsMinimized(false);
    } catch (err) {
      console.error('PDF 분석 오류:', err);
      // 에러 메시지가 여러 줄인 경우 처리
      const errorMessage = err.message || 'PDF 분석 중 오류가 발생했습니다.';
      setError(errorMessage.split('\n').join(' ')); // 줄바꿈을 공백으로 변환
      // totalFiles는 try 블록에서 선언되었으므로, 여기서는 allFiles.length 사용
      const errorTotalFiles = sections.reduce((sum, s) => sum + s.files.length, 0);
      setProgressInfo({ current: 0, total: errorTotalFiles, stage: '오류' });
    } finally {
      setIsAnalyzing(false);
    }
  }, [sections]);

  // 작업 중 창 닫기/나가기 방지
  useEffect(() => {
    if (isAnalyzing) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'PDF 분석이 진행 중입니다. 정말 나가시겠습니까?';
        return e.returnValue;
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isAnalyzing]);

  // 모달 닫기 시 확인 (작업 중이면 경고)
  const handleClose = () => {
    if (isAnalyzing) {
      if (window.confirm('PDF 분석이 진행 중입니다. 정말 닫으시겠습니까?\n\n작업이 중단될 수 있습니다.')) {
        setIsAnalyzing(false);
        setIsMinimized(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  // 최소화된 상태 UI
  if (isMinimized && isAnalyzing) {
    const progressPercent = progressInfo.total > 0 
      ? Math.round((progressInfo.current / progressInfo.total) * 100) 
      : 0;
    
    return (
      <div 
        className="fixed bottom-4 right-4 bg-white shadow-2xl rounded-lg border-2 border-orange-500 z-50 cursor-pointer hover:shadow-3xl transition-shadow"
        onClick={() => setIsMinimized(false)}
        style={{ minWidth: '200px' }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-800">PDF 분석 진행 중</h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="닫기"
            >
              ×
            </button>
          </div>
          <div className="mb-2">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>{progressInfo.stage}</span>
              <span>{progressInfo.current} / {progressInfo.total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center">클릭하여 확대</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">PDF 업로드</h2>
            <div className="flex items-center gap-2">
              {isAnalyzing && (
                <button
                  onClick={() => setIsMinimized(true)}
                  className="text-gray-400 hover:text-gray-600 text-lg px-2 py-1 rounded hover:bg-gray-100"
                  aria-label="최소화"
                  title="최소화"
                >
                  ─
                </button>
              )}
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="닫기"
              >
                ×
              </button>
            </div>
          </div>

          {!parsedData ? (
            <div className="space-y-4">
              {/* 구역 목록 - 그리드 레이아웃으로 간결하게 표시 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {sections.map((section, sectionIndex) => {
                  const isExpanded = expandedSectionId === section.id;
                  const hasFiles = section.files.length > 0;
                  
                  return (
                    <div 
                      key={section.id} 
                      className={`border border-gray-300 rounded-lg p-3 bg-white hover:shadow-md transition-shadow ${
                        isDragging && draggingSectionId === section.id ? 'border-orange-500 bg-orange-50' : ''
                      }`}
                      onDragEnter={(e) => handleDragEnter(e, section.id)}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, section.id)}
                    >
                      {/* 상권 선택 */}
                      <div className="mb-2">
                        {isLoading상권목록 ? (
                          <div className="w-full px-2 py-1.5 border border-gray-300 rounded bg-gray-50 flex items-center gap-2">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500"></div>
                            <span className="text-xs text-gray-500">로딩 중...</span>
                          </div>
                        ) : (
                          <select
                            id={`상권-select-${section.id}`}
                            value={section.상권}
                            onChange={(e) => updateSection상권(section.id, e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
                            disabled={isAnalyzing}
                          >
                            <option value="">상권 선택</option>
                            {상권목록.map((상권명) => (
                              <option key={상권명} value={상권명}>
                                {상권명}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* 상권명과 파일 개수 표시 */}
                      {section.상권 && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {section.상권}
                              </p>
                              <p className="text-xs text-gray-500">
                                {section.files.length}개 파일
                              </p>
                            </div>
                            {sections.length > 1 && (
                              <button
                                onClick={() => removeSection(section.id)}
                                className="ml-2 text-red-500 hover:text-red-700 text-lg leading-none px-1"
                                disabled={isAnalyzing}
                                aria-label="구역 삭제"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 파일명 보기 버튼 및 드롭다운 */}
                      {hasFiles && (
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => setExpandedSectionId(isExpanded ? null : section.id)}
                            className="w-full text-left px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded flex items-center justify-between"
                            disabled={isAnalyzing}
                          >
                            <span>파일명 보기 ({section.files.length}개)</span>
                            <svg
                              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded max-h-40 overflow-y-auto">
                              <ul className="text-xs text-gray-700 space-y-1">
                                {section.files.map((file, fileIndex) => (
                                  <li key={fileIndex} className="flex items-center justify-between group">
                                    <span className="truncate flex-1 pr-2">{file.name}</span>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className="text-gray-500">{(file.size / 1024).toFixed(1)}KB</span>
                                      {!isAnalyzing && (
                                        <button
                                          onClick={() => removeFileFromSection(section.id, fileIndex)}
                                          className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                          aria-label="파일 제거"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* 파일 추가 영역 (드래그 앤 드롭 지원) */}
                      <div 
                        className={`mt-2 border-2 border-dashed rounded-lg transition-all ${
                          isDragging && draggingSectionId === section.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        } ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        onDragEnter={(e) => handleDragEnter(e, section.id)}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, section.id)}
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileSelect(e, section.id)}
                          className="hidden"
                          id={`pdf-upload-input-${section.id}`}
                          multiple
                          disabled={isAnalyzing}
                        />
                        <label
                          htmlFor={`pdf-upload-input-${section.id}`}
                          className={`block w-full px-4 py-6 text-center ${
                            isAnalyzing ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          {isDragging && draggingSectionId === section.id ? (
                            <div className="flex flex-col items-center gap-2">
                              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <span className="text-sm font-medium text-orange-600">여기에 파일을 놓으세요</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <div>
                                <span className="text-sm font-medium text-gray-700">
                                  {hasFiles ? '파일 추가' : '파일 선택 또는 드래그'}
                                </span>
                                <p className="text-xs text-gray-500 mt-1">PDF 파일만 가능</p>
                              </div>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 상권 추가 버튼 */}
              <div className="flex justify-center">
                <button
                  onClick={addSection}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors flex items-center gap-2"
                  disabled={isAnalyzing}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  상권 추가
                </button>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* 전체 파일 수 계산 */}
              {(() => {
                const totalFiles = sections.reduce((sum, s) => sum + s.files.length, 0);
                const validSections = sections.filter(s => s.상권.trim() && s.files.length > 0).length;
                return totalFiles > 0 && (
                  <div className="p-2 bg-orange-50 border border-orange-200 rounded text-center">
                    <p className="text-sm text-orange-800 font-semibold">
                      총 {validSections}개 상권, {totalFiles}개 파일
                    </p>
                  </div>
                );
              })()}

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setIsMigrationModalOpen(true)}
                  className="px-4 py-2 bg-gray-500 text-white rounded text-sm font-semibold hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isAnalyzing}
                >
                  해시 정보 마이그레이션
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    disabled={isAnalyzing}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || sections.every(s => s.files.length === 0 || !s.상권.trim())}
                    className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing 
                      ? `분석 중... (${sections.reduce((sum, s) => sum + s.files.length, 0)}개 파일)` 
                      : `분석 및 확인 (${sections.reduce((sum, s) => sum + s.files.length, 0)}개 파일)`}
                  </button>
                </div>
              </div>

              {/* 분석 중 표시 */}
              {isAnalyzing && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    <p className="text-sm text-blue-800">
                      PDF 파일을 분석하고 있습니다. 잠시만 기다려주세요...
                    </p>
                  </div>
                  {progressInfo.total > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-blue-700 mb-1">
                        <span>{progressInfo.stage}</span>
                        <span>{progressInfo.current} / {progressInfo.total} ({Math.round((progressInfo.current / progressInfo.total) * 100)}%)</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.round((progressInfo.current / progressInfo.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 진행 상황 팝업 - 더 이상 사용되지 않음 (PdfAnalysisModal에서 처리) */}
              {false && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-[60] flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-800">처리 진행 상황</h3>
                        <div className="text-sm text-gray-600">
                          {progress.current} / {progress.total}
                        </div>
                      </div>

                      {/* 전체 진행 바 */}
                      <div className="mb-6">
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div
                            className="bg-orange-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 text-center">
                          {progress.currentFile && (
                            <span className="font-semibold">{progress.currentFile}</span>
                          )}
                          {progress.stage === 'parsing' && ' - PDF 파싱 중...'}
                          {progress.stage === 'formalizing' && ' - Gemini 정형화 중...'}
                          {progress.stage === 'saving' && ' - Firestore 저장 중...'}
                          {progress.stage === 'completed' && ' - 완료!'}
                        </p>
                      </div>

                      {/* 파일별 진행 상황 */}
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {progress.results.map((result, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border-2 ${
                              result.status === 'success'
                                ? 'bg-green-50 border-green-200'
                                : result.status === 'error'
                                ? 'bg-red-50 border-red-200'
                                : result.status === 'processing'
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {index + 1}. {result.fileName}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {result.message}
                                  {result.상권 && (
                                    <span className="ml-2 text-gray-500">(상권: {result.상권})</span>
                                  )}
                                </p>
                                {result.stage && result.stage !== 'completed' && result.stage !== 'error' && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                      <div 
                                        className={`h-1.5 rounded-full transition-all duration-300 ${
                                          result.stage === 'parsing' ? 'bg-blue-500 w-1/3' :
                                          result.stage === 'formalizing' ? 'bg-purple-500 w-2/3' :
                                          result.stage === 'saving' ? 'bg-orange-500 w-full' :
                                          'bg-gray-300 w-0'
                                        }`}
                                      ></div>
                                    </div>
                                    <span className="text-[0.875rem] text-gray-500">
                                      {result.stage === 'parsing' && '파싱 중'}
                                      {result.stage === 'formalizing' && '정형화 중'}
                                      {result.stage === 'saving' && '저장 중'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-3">
                                {result.status === 'success' && (
                                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                                {result.status === 'error' && (
                                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                )}
                                {result.status === 'processing' && (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                                )}
                                {result.status === 'pending' && (
                                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {progress.stage === 'completed' && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800 font-semibold">
                            ✓ 모든 파일 처리가 완료되었습니다!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : parsedData && parsedData.properties ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800 font-semibold mb-2">
                  ✓ 정형화 및 저장 완료
                </p>
                <div className="text-xs text-green-700 space-y-1">
                  <p><span className="font-medium">저장된 매물:</span> {parsedData.saved}개 / {parsedData.total}개</p>
                  {parsedData.errors && parsedData.errors.length > 0 && (
                    <p className="text-red-600">
                      <span className="font-medium">오류:</span> {parsedData.errors.length}개
                    </p>
                  )}
                </div>
              </div>

              {/* 구역별 저장된 매물 목록 */}
              {parsedData.sections && Object.entries(parsedData.sections).map(([상권, properties]) => (
                <div key={상권} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    상권: {상권} ({properties.length}개)
                  </h3>
                  <div className="space-y-2">
                    {properties.map((property, index) => (
                      <div key={index} className="border border-gray-200 rounded p-3 bg-white">
                        <h4 className="text-xs font-semibold text-gray-700 mb-1">
                          매물 {index + 1}: {property.상호 || property.파일명 || `ID ${property.id}`}
                        </h4>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <p><span className="font-medium">ID:</span> {property.id}</p>
                          <p><span className="font-medium">주소:</span> {property.주소 || '-'}</p>
                          <p><span className="font-medium">보증금:</span> {property.보증금 || '-'}</p>
                          <p><span className="font-medium">월세:</span> {property.월세 || '-'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* 오류 목록 */}
              {parsedData.errors && parsedData.errors.length > 0 && (
                <div className="border border-red-200 rounded p-4 bg-red-50">
                  <h3 className="text-sm font-semibold text-red-700 mb-2">오류 발생 파일</h3>
                  <ul className="text-xs text-red-600 space-y-1">
                    {parsedData.errors.map((err, index) => (
                      <li key={index}>
                        {err.fileName}: {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setParsedData(null);
                    setSections([{ id: 1, 상권: '', files: [] }]);
                    setProgress({
                      current: 0,
                      total: 0,
                      currentFile: '',
                      stage: 'idle',
                      results: []
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  새로 업로드
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600"
                >
                  닫기
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800 font-semibold mb-2">
                  ✓ PDF 파싱 완료 ({parsedData?.totalFiles || 0}개 파일)
                </p>
                <div className="text-xs text-green-700 space-y-1">
                  <p><span className="font-medium">적용된 상권:</span> {parsedData?.상권}</p>
                  <p><span className="font-medium">파싱된 파일 수:</span> {parsedData?.totalFiles || 0}개</p>
                </div>
              </div>

              {/* 각 파일별 파싱 결과 */}
              {parsedData.files && parsedData.files.map((fileData, index) => (
                <div key={index} className="border border-gray-200 rounded p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    파일 {index + 1}: {fileData.fileName}
                  </h3>
                  <div className="text-xs text-gray-600 space-y-1 mb-3">
                    <p><span className="font-medium">페이지 수:</span> {fileData.numPages}페이지</p>
                    <p><span className="font-medium">텍스트 길이:</span> {fileData.textLength?.toLocaleString()}자</p>
                    <p><span className="font-medium">적용된 상권:</span> {parsedData.상권}</p>
                  </div>

                  {/* 메타데이터 정보 */}
                  {fileData.metadata && Object.keys(fileData.metadata).length > 0 && (
                    <details className="mb-3">
                      <summary className="text-xs font-medium text-gray-600 cursor-pointer mb-2">
                        PDF 메타데이터
                      </summary>
                      <div className="text-xs text-gray-600 space-y-1 pl-4">
                        {Object.entries(fileData.metadata).map(([key, value]) => (
                          <p key={key}>
                            <span className="font-medium">{key}:</span> {String(value)}
                          </p>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* 미리보기 */}
                  <details className="mb-3">
                    <summary className="text-xs font-medium text-gray-600 cursor-pointer mb-2">
                      텍스트 미리보기 (처음 500자)
                    </summary>
                    <div className="bg-gray-50 p-3 rounded text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {fileData.preview}
                      {fileData.textLength > 500 && '...'}
                    </div>
                  </details>

                  {/* 전체 텍스트 (줄 단위) */}
                  <details className="mb-3">
                    <summary className="text-xs font-medium text-gray-600 cursor-pointer mb-2">
                      파싱된 텍스트 (줄 단위, {fileData.textLines?.length || 0}줄)
                    </summary>
                    <div className="bg-gray-50 p-3 rounded text-xs text-gray-700 max-h-96 overflow-y-auto">
                      {fileData.textLines?.map((line, lineIndex) => (
                        <div key={lineIndex} className="mb-1 border-b border-gray-200 pb-1">
                          <span className="text-gray-400 mr-2">#{lineIndex + 1}</span>
                          {line}
                        </div>
                      ))}
                    </div>
                  </details>

                  {/* 전체 텍스트 (원본) */}
                  <details>
                    <summary className="text-xs font-medium text-gray-600 cursor-pointer mb-2">
                      전체 텍스트 (원본)
                    </summary>
                    <div className="bg-gray-50 p-3 rounded text-xs text-gray-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {fileData.text}
                    </div>
                  </details>
                </div>
              ))}

              {/* JSON 형식으로 보기 */}
              <details className="border border-gray-200 rounded p-4">
                <summary className="text-sm font-semibold text-gray-700 cursor-pointer">
                  JSON 형식으로 보기
                </summary>
                <pre className="mt-2 bg-gray-50 p-3 rounded text-xs text-gray-700 overflow-x-auto">
                  {JSON.stringify(parsedData, null, 2)}
                </pre>
              </details>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setParsedData(null);
                    setSections([{ id: 1, 상권: '', files: [] }]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  새로 업로드
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600"
                >
                  닫기
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 분석 결과 모달 */}
      {analysisResult && (
        <PdfAnalysisModal
          analysisResult={analysisResult}
          onClose={() => {
            setAnalysisResult(null);
            // 분석 후 성공 시 파일 목록 초기화
            setSections([{ id: 1, 상권: '', files: [] }]);
          }}
          onSuccess={(result) => {
            // 처리 완료 후 성공 콜백
            if (onSuccess) {
              onSuccess();
            }
            // 결과 표시
            setParsedData({
              sections: {},
              saved: result.saved || 0,
              total: result.total || 0,
              properties: result.properties || [],
              errors: result.errors || []
            });
            setAnalysisResult(null);
          }}
        />
      )}

      {/* 마이그레이션 모달 */}
      {isMigrationModalOpen && (
        <PdfMigrationModal
          onClose={() => setIsMigrationModalOpen(false)}
          onSuccess={() => {
            setIsMigrationModalOpen(false);
            if (onSuccess) {
              onSuccess();
            }
          }}
        />
      )}
    </div>
  );
}

export default PdfUploadTest;

